/**
 * ReminderBanner — on-login follow-up banner for clinical staff (Phase 5e,
 * Jun 11 2026 — Ana's Operations workflow spec).
 *
 * On mount, calls patient-reminders:summary-for-user (Phase 5d backend).
 * Renders a dismissible banner summarizing the assigned PEM's due
 * follow-ups + pending patient-requested follow-ups + unassigned pool.
 * Clicking a card navigates to the relevant surface; the banner itself is
 * session-dismissible (it reappears next login because the underlying
 * reminders are still open — that's intentional, it's a worklist).
 *
 * Only renders for roles that hold patient_reminders:read (PEM, physician,
 * sysadmin). Silent no-op for everyone else, and silent no-op when there's
 * nothing due — staff with an empty queue see no banner.
 *
 * Lightweight by design: text + count chips + up to a few cards. Lives
 * outside PageErrorBoundary so a render bug can't knock out the page.
 */
import React, { useState, useEffect, useCallback } from "react";
import { Bell, X, Clock, AlertTriangle, CalendarClock } from "lucide-react";

const CAN_SEE = (user) => {
  const perms = new Set(user?.permissions || []);
  return perms.has("patient_reminders:read");
};

export default function ReminderBanner({ user, onNavigate }) {
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!user || !CAN_SEE(user)) return;
    try {
      const r = await window.zignagenixAPI?.invoke?.("patient-reminders:summary-for-user");
      if (r && !r.error) setSummary(r);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[ReminderBanner] summary-for-user skipped:", e?.message);
      }
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const snooze = useCallback(async (id, minutes) => {
    setBusyId(id);
    try {
      await window.zignagenixAPI?.invoke?.("patient-reminders:snooze", { id, minutes });
      await load();
    } catch (_) { /* non-fatal */ } finally { setBusyId(null); }
  }, [load]);

  const dismiss = useCallback(async (id) => {
    setBusyId(id);
    try {
      await window.zignagenixAPI?.invoke?.("patient-reminders:dismiss", { id });
      await load();
    } catch (_) { /* non-fatal */ } finally { setBusyId(null); }
  }, [load]);

  if (!user || !CAN_SEE(user) || dismissed || !summary) return null;

  const { overdue = 0, dueToday = 0, upcoming = 0, pendingRequests = 0, unassignedRequests = 0, cards = [] } = summary;
  const totalActionable = overdue + dueToday + pendingRequests;
  // Nothing pressing and nothing pending → don't show the banner at all.
  if (totalActionable === 0 && upcoming === 0 && unassignedRequests === 0) return null;

  const chip = (icon, label, count, bg, fg) => count > 0 ? (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 999, background: bg, color: fg,
      fontSize: 12, fontWeight: 700,
    }}>{icon} {count} {label}</span>
  ) : null;

  return (
    <div
      role="region"
      aria-label="Follow-up reminders"
      style={{
        position: "relative", margin: "16px 24px 8px", padding: "14px 18px",
        background: "linear-gradient(135deg, #FFF7ED 0%, #F8FAFC 100%)",
        border: "1px solid #F59E0B", borderRadius: 12,
        boxShadow: "0 2px 8px rgba(245, 158, 11, 0.12)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{
          background: "#F59E0B", color: "white", width: 36, height: 36, borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <Bell size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#212529", marginBottom: 6 }}>
            {overdue > 0
              ? `${overdue} follow-up${overdue > 1 ? "s" : ""} overdue — review and call`
              : dueToday > 0
                ? `${dueToday} follow-up${dueToday > 1 ? "s" : ""} due today`
                : pendingRequests > 0
                  ? `${pendingRequests} patient-requested follow-up${pendingRequests > 1 ? "s" : ""} awaiting review`
                  : "Your follow-up queue"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: cards.length ? 10 : 0 }}>
            {chip(<AlertTriangle size={12} />, "overdue", overdue, "#FEE2E2", "#B91C1C")}
            {chip(<Clock size={12} />, "due today", dueToday, "#FEF3C7", "#92400E")}
            {chip(<CalendarClock size={12} />, "upcoming", upcoming, "#E0F2FE", "#075985")}
            {chip(<Bell size={12} />, "requested", pendingRequests, "#EDE9FE", "#5B21B6")}
            {chip(<Bell size={12} />, "unassigned", unassignedRequests, "#F1F5F9", "#475569")}
          </div>

          {cards.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {cards.map((c) => (
                <div key={c.id} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                  background: "white", border: "1px solid #FDE68A", borderRadius: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1F2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.card?.title || "Follow-up"}
                    </div>
                    <div style={{ fontSize: 11, color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.card?.subtitle || c.contact_name || ""}
                    </div>
                  </div>
                  <button
                    onClick={() => onNavigate && onNavigate(c.source_request_id ? "pemdashboard" : "pemdashboard")}
                    style={ctaBtn}
                  >
                    {c.card?.cta || "Open"}
                  </button>
                  <button onClick={() => snooze(c.id, 60 * 24)} disabled={busyId === c.id} title="Snooze 1 day" style={ghostBtn}>
                    Snooze
                  </button>
                  <button onClick={() => dismiss(c.id)} disabled={busyId === c.id} title="Dismiss" style={ghostBtn}>
                    Done
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Hide reminders banner"
          title="Hide for now"
          style={{ ...ghostBtn, flexShrink: 0 }}
        >
          <X size={12} /> Hide
        </button>
      </div>
    </div>
  );
}

const ctaBtn = {
  background: "#2E8B98", color: "white", border: "none", padding: "5px 12px",
  borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", flexShrink: 0,
};
const ghostBtn = {
  background: "white", border: "1px solid #DEE2E6", color: "#6C757D",
  padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600,
  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
};
